import { createFileRoute, Link } from '@tanstack/react-router'
import { useMemo, useState, useCallback } from "react";
import { useQuery } from "@tanstack/react-query";
import {
  TrendingUp,
  Users,
  UserCheck,
  Filter,
  CalendarDays,
  Activity,
  AlertTriangle,
  FileText,
  Building2,
  Clock,
  Download,
  Share2,
} from "lucide-react";
import {
  PieChart,
  Pie,
  Cell,
  ResponsiveContainer,
  Tooltip,
  Legend,
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
} from "recharts";

import { Header } from "@/components/layout/Header";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  getEntityStats,
  getActivityTimeline,
  getTopUsers,
  getRecentActivity,
  getAgencyStats,
  type TimelinePoint,
} from "@/lib/api/stats";
import { listSimulators } from "@/lib/api/simulators";
import { ViewerGuard } from "@/lib/auth/AdminGuard";
import { OfferRegionStatsCard } from "@/components/dashboard/OfferRegionStatsCard";
import { UserFilter } from "@/components/dashboard/UserFilter";
import { useIsUser } from "@/lib/auth/store";


export const Route = createFileRoute('/_dashboard/')({
  component: DashboardPage,
})

const PIE_COLORS = [
  "#F2A900",
  "#003DA5",
  "#00B140",
  "#0072CE",
  "#E5002B",
  "#8B5CF6",
  "#F59E0B",
  "#06B6D4",
  "#EC4899",
  "#10B981",
];

const CHART_COLORS = {
  simulations: "#F2A900",
  cotations: "#003DA5",
  proposals: "#00B140",
};

function formatDateLabel(isoDate: string, granularity: string): string {
  const d = new Date(isoDate);
  if (granularity === "month") return d.toLocaleDateString("fr-FR", { month: "short", year: "2-digit" });
  if (granularity === "week") return d.toLocaleDateString("fr-FR", { day: "2-digit", month: "short" });
  return d.toLocaleDateString("fr-FR", { day: "2-digit", month: "short" });
}

type Preset = { label: string; from: string; to: string };

function getPresets(): Preset[] {
  const now = new Date();
  const fmt = (d: Date) => d.toISOString().split("T")[0];
  const startOfWeek = new Date(now);
  startOfWeek.setDate(now.getDate() - now.getDay() + 1);
  const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);
  const startOfQuarter = new Date(now.getFullYear(), Math.floor(now.getMonth() / 3) * 3, 1);
  const startOfYear = new Date(now.getFullYear(), 0, 1);

  return [
    { label: "Aujourd'hui", from: fmt(now), to: fmt(now) },
    { label: "Cette semaine", from: fmt(startOfWeek), to: fmt(now) },
    { label: "Ce mois", from: fmt(startOfMonth), to: fmt(now) },
    { label: "Ce trimestre", from: fmt(startOfQuarter), to: fmt(now) },
    { label: "Cette année", from: fmt(startOfYear), to: fmt(now) },
  ];
}

function DashboardPage() {
  // Les BE (rôle user) n'ont pas accès au suivi des utilisateurs : on masque
  // les éléments orientés utilisateurs de leur dashboard.
  const isBE = useIsUser();
  const [simulatorFilter, setSimulatorFilter] = useState<string>("all");
  const [userFilter, setUserFilter] = useState<string>("all");
  const [dateFrom, setDateFrom] = useState<string>("");
  const [dateTo, setDateTo] = useState<string>("");
  const [activePresetLabel, setActivePresetLabel] = useState<string | undefined>(undefined);
  const [granularity, setGranularity] = useState<"day" | "week" | "month">("day");

  const presets = useMemo(() => getPresets(), []);

  const filterParams = useMemo(() => ({
    simulator_id: simulatorFilter !== "all" ? parseInt(simulatorFilter) : undefined,
    user_id: userFilter !== "all" ? parseInt(userFilter) : undefined,
    date_from: dateFrom || undefined,
    date_to: dateTo || undefined,
  }), [simulatorFilter, userFilter, dateFrom, dateTo]);

  const { data: simulatorsData } = useQuery({
    queryKey: ["simulators-list"],
    queryFn: () => listSimulators({ page_size: 100, include_unpublished: true }),
  });

  const { data: entityStats, isLoading: isLoadingStats } = useQuery({
    queryKey: ["entity-stats", filterParams],
    queryFn: () => getEntityStats(filterParams),
  });

  const { data: timeline, isLoading: isLoadingTimeline } = useQuery({
    queryKey: ["activity-timeline", filterParams, granularity],
    queryFn: () => getActivityTimeline({ ...filterParams, granularity }),
  });

  const { data: topUsers, isLoading: isLoadingTopUsers } = useQuery({
    queryKey: ["top-users", filterParams],
    queryFn: () => getTopUsers({
      simulator_id: filterParams.simulator_id,
      date_from: filterParams.date_from,
      date_to: filterParams.date_to,
      limit: 10,
    }),
  });

  const { data: recentActivity, isLoading: isLoadingActivity } = useQuery({
    queryKey: ["recent-activity", filterParams],
    queryFn: () => getRecentActivity({ limit: 15, ...filterParams }),
  });

  const { data: agencyStats, isLoading: isLoadingAgency } = useQuery({
    queryKey: ["agency-stats", filterParams],
    queryFn: () => getAgencyStats(filterParams),
  });

  const resetFilters = () => {
    setSimulatorFilter("all");
    setUserFilter("all");
    setDateFrom("");
    setDateTo("");
    setActivePresetLabel(undefined);
  };

  const applyPreset = (preset: Preset) => {
    setDateFrom(preset.from);
    setDateTo(preset.to);
    setActivePresetLabel(preset.label);
  };

  const hasActiveFilters =
    simulatorFilter !== "all" ||
    userFilter !== "all" ||
    dateFrom !== "" ||
    dateTo !== "";

  const allStats = [
    {
      title: "Simulations totales",
      value: entityStats?.totals.simulation_events ?? 0,
      icon: Activity,
      color: "text-laposte-yellow",
      bgColor: "bg-laposte-yellow/10",
      href: "/admin/simulation-events",
    },
    {
      title: "Cotations totales",
      value: entityStats?.totals.simulations ?? 0,
      icon: TrendingUp,
      color: "text-success",
      bgColor: "bg-success/10",
      href: "/admin/simulations",
    },
    {
      title: "Propositions totales",
      value: entityStats?.totals.proposals ?? 0,
      icon: FileText,
      color: "text-laposte-navy",
      bgColor: "bg-laposte-navy/10",
      href: "/admin/proposals",
    },
    {
      title: "Cotations hors politique",
      value: entityStats?.totals.hors_politique_simulations ?? 0,
      icon: AlertTriangle,
      color: "text-destructive",
      bgColor: "bg-destructive/10",
      href: "/admin/simulations",
      search: { policyFilter: "hors_politique" },
    },
    {
      title: "Cotations partagées",
      value: entityStats?.totals.shared_simulations ?? 0,
      icon: Share2,
      color: "text-laposte-navy",
      bgColor: "bg-laposte-navy/10",
      href: "/admin/simulations",
      search: { shared: "shared" as const },
    },
    {
      title: "Utilisateurs créés",
      value: entityStats?.totals.users ?? 0,
      icon: Users,
      color: "text-laposte-navy",
      bgColor: "bg-laposte-navy/10",
      href: "/users",
    },
    {
      title: "Utilisateurs actifs",
      value: entityStats?.totals.active_users ?? 0,
      description: "Connectés au moins 1 fois",
      icon: UserCheck,
      color: "text-info",
      bgColor: "bg-info/10",
      href: "/admin/user-activity",
    },
  ];

  // Pour les BE, on retire les KPIs renvoyant vers des écrans utilisateurs fermés.
  const stats = isBE
    ? allStats.filter(
        (s) => s.title !== "Utilisateurs créés" && s.title !== "Utilisateurs actifs",
      )
    : allStats;

  const cotationsBySimulator = entityStats?.cotations_by_simulator ?? [];
  const simulationsBySimulator = entityStats?.simulations_by_simulator ?? [];

  const simulationsPieData = simulationsBySimulator
    .filter((s) => s.events_count > 0)
    .map((s) => ({ name: s.simulator_name, value: s.events_count }));

  const cotationsPieData = cotationsBySimulator
    .filter((s) => s.cotations_count > 0)
    .map((s) => ({ name: s.simulator_name, value: s.cotations_count }));

  const proposalsPieData = cotationsBySimulator
    .filter((s) => s.proposals_count > 0)
    .map((s) => ({ name: s.simulator_name, value: s.proposals_count }));

  const colorMap = useMemo(() => {
    const allNames = new Set<string>();
    simulationsPieData.forEach((d) => allNames.add(d.name));
    cotationsPieData.forEach((d) => allNames.add(d.name));
    proposalsPieData.forEach((d) => allNames.add(d.name));
    const map: Record<string, string> = {};
    let i = 0;
    allNames.forEach((name) => {
      map[name] = PIE_COLORS[i % PIE_COLORS.length];
      i++;
    });
    return map;
  }, [simulationsPieData, cotationsPieData, proposalsPieData]);

  const mergedTableData = useMemo(() => {
    const map = new Map<string, { simulations: number; cotations: number; proposals: number }>();
    simulationsBySimulator.forEach((s) => {
      map.set(s.simulator_name, { simulations: s.events_count, cotations: 0, proposals: 0 });
    });
    cotationsBySimulator.forEach((s) => {
      const existing = map.get(s.simulator_name) ?? { simulations: 0, cotations: 0, proposals: 0 };
      existing.cotations = s.cotations_count;
      existing.proposals = s.proposals_count;
      map.set(s.simulator_name, existing);
    });
    return Array.from(map.entries()).map(([name, data]) => ({ name, ...data }));
  }, [simulationsBySimulator, cotationsBySimulator]);

  const timelineData = useMemo(
    () =>
      (timeline ?? []).map((p: TimelinePoint) => ({
        ...p,
        label: formatDateLabel(p.period, granularity),
      })),
    [timeline, granularity],
  );

  const exportCsv = useCallback(() => {
    if (!mergedTableData.length) return;
    const header = "Simulateur;Simulations;Cotations;Propositions\n";
    const rows = mergedTableData.map(
      (r) => `${r.name};${r.simulations};${r.cotations};${r.proposals}`,
    );
    // Sans BOM, Excel lit le fichier en Windows-1252 et abîme les accents.
    const blob = new Blob(["\uFEFF" + header + rows.join("\n")], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `dashboard-export-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  }, [mergedTableData]);

  return (
    <ViewerGuard>
    <div className="flex flex-col h-full">
      <Header title="Dashboard" />

      <div className="flex-1 p-6 space-y-6 overflow-auto">
        {/* Filters */}
        <Card>
          <CardContent className="p-4 space-y-3">
            <div className="flex flex-wrap items-center gap-4">
              <div className="flex items-center gap-2 text-muted-foreground">
                <Filter className="h-4 w-4" />
                <span className="text-sm font-medium">Filtres</span>
              </div>

              <Select value={simulatorFilter} onValueChange={setSimulatorFilter}>
                <SelectTrigger className="w-[200px]">
                  <SelectValue placeholder="Simulateur" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tous les simulateurs</SelectItem>
                  {simulatorsData?.items.map((sim) => (
                    <SelectItem key={sim.id} value={String(sim.id)}>
                      {sim.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>

              <UserFilter value={userFilter} onChange={setUserFilter} />

              <div className="flex items-center gap-2">
                <CalendarDays className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                <div className="flex items-center gap-1.5">
                  <Label htmlFor="dash-date-from" className="sr-only">Du</Label>
                  <Input
                    id="dash-date-from"
                    type="date"
                    value={dateFrom}
                    onChange={(e) => { setDateFrom(e.target.value); setActivePresetLabel(undefined); }}
                    className="h-9 w-[140px] text-sm"
                  />
                  <span className="text-xs text-muted-foreground">–</span>
                  <Label htmlFor="dash-date-to" className="sr-only">Au</Label>
                  <Input
                    id="dash-date-to"
                    type="date"
                    value={dateTo}
                    onChange={(e) => { setDateTo(e.target.value); setActivePresetLabel(undefined); }}
                    className="h-9 w-[140px] text-sm"
                  />
                </div>
              </div>

              {hasActiveFilters && (
                <Button variant="ghost" size="sm" onClick={resetFilters}>
                  Réinitialiser
                </Button>
              )}

              <div className="ml-auto">
                <Button variant="outline" size="sm" onClick={exportCsv} disabled={!mergedTableData.length}>
                  <Download className="h-4 w-4 mr-1.5" />
                  CSV
                </Button>
              </div>
            </div>

            {/* Date presets */}
            <div className="flex flex-wrap gap-2">
              {presets.map((p) => (
                <Button
                  key={p.label}
                  variant={activePresetLabel === p.label ? "default" : "outline"}
                  size="sm"
                  className="h-7 text-xs"
                  onClick={() => applyPreset(p)}
                >
                  {p.label}
                </Button>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* KPI cards */}
        <div className="grid gap-4 grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6">
          {stats.map((stat, index) => (
            <Link
              key={stat.title}
              to={stat.href}
              search={stat.search ?? {}}
              className="block h-full"
              style={{ animation: `fadeInUp 0.5s ease-out ${index * 0.1}s both` }}
            >
              <Card className="relative overflow-hidden transition-shadow hover:shadow-lg hover:ring-1 hover:ring-primary/20 cursor-pointer h-full">
                <CardHeader className="flex flex-row items-center justify-between pb-2">
                  <div>
                    <CardTitle className="text-sm font-medium text-muted-foreground">
                      {stat.title}
                    </CardTitle>
                    {stat.description && (
                      <p className="text-[10px] text-muted-foreground/70 mt-0.5">{stat.description}</p>
                    )}
                  </div>
                  <div className={`p-2 rounded-lg ${stat.bgColor}`}>
                    <stat.icon className={`h-4 w-4 ${stat.color}`} />
                  </div>
                </CardHeader>
                <CardContent>
                  {isLoadingStats ? (
                    <Skeleton className="h-9 w-16" />
                  ) : (
                    <div className="text-3xl font-bold">
                      {typeof stat.value === "number" ? stat.value.toLocaleString("fr-FR") : stat.value}
                    </div>
                  )}
                </CardContent>
                <div className="absolute bottom-0 left-0 right-0 h-1 bg-gradient-to-r from-transparent via-primary/20 to-transparent" />
              </Card>
            </Link>
          ))}
        </div>

        {/* Activity timeline */}
        <div style={{ animation: "fadeInUp 0.5s ease-out 0.1s both" }}>
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <div>
                <CardTitle>Évolution de l'activité</CardTitle>
                <CardDescription>Simulations, cotations et propositions dans le temps</CardDescription>
              </div>
              <Select value={granularity} onValueChange={(v) => setGranularity(v as "day" | "week" | "month")}>
                <SelectTrigger className="w-[130px]">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="day">Par jour</SelectItem>
                  <SelectItem value="week">Par semaine</SelectItem>
                  <SelectItem value="month">Par mois</SelectItem>
                </SelectContent>
              </Select>
            </CardHeader>
            <CardContent>
              {isLoadingTimeline ? (
                <Skeleton className="h-[300px] w-full" />
              ) : timelineData.length === 0 ? (
                <p className="text-center py-12 text-muted-foreground">Aucune donnée disponible</p>
              ) : (
                <ResponsiveContainer width="100%" height={300}>
                  <AreaChart data={timelineData}>
                    <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                    <XAxis dataKey="label" tick={{ fontSize: 12 }} className="text-muted-foreground" />
                    <YAxis tick={{ fontSize: 12 }} className="text-muted-foreground" />
                    <Tooltip
                      contentStyle={{ borderRadius: 8, border: "1px solid hsl(var(--border))", background: "hsl(var(--card))" }}
                    />
                    <Legend />
                    <Area
                      type="monotone"
                      dataKey="simulations"
                      name="Simulations"
                      stroke={CHART_COLORS.simulations}
                      fill={CHART_COLORS.simulations}
                      fillOpacity={0.15}
                      strokeWidth={2}
                    />
                    <Area
                      type="monotone"
                      dataKey="cotations"
                      name="Cotations"
                      stroke={CHART_COLORS.cotations}
                      fill={CHART_COLORS.cotations}
                      fillOpacity={0.15}
                      strokeWidth={2}
                    />
                    <Area
                      type="monotone"
                      dataKey="proposals"
                      name="Propositions"
                      stroke={CHART_COLORS.proposals}
                      fill={CHART_COLORS.proposals}
                      fillOpacity={0.15}
                      strokeWidth={2}
                    />
                  </AreaChart>
                </ResponsiveContainer>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Table by simulator */}
        <div style={{ animation: "fadeInUp 0.5s ease-out 0.2s both" }}>
          <Card>
            <CardHeader>
              <CardTitle>Détail par simulateur</CardTitle>
              <CardDescription>Nombre de simulations, cotations et propositions commerciales par simulateur</CardDescription>
            </CardHeader>
            <CardContent>
              {isLoadingStats ? (
                <div className="space-y-3">
                  {[...Array(3)].map((_, i) => (
                    <Skeleton key={i} className="h-10 w-full" />
                  ))}
                </div>
              ) : mergedTableData.length === 0 ? (
                <p className="text-center py-6 text-muted-foreground">Aucune donnée disponible</p>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Simulateur</TableHead>
                      <TableHead className="text-right">Simulations</TableHead>
                      <TableHead className="text-right">Cotations</TableHead>
                      <TableHead className="text-right">Propositions commerciales</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {mergedTableData.map((row) => (
                      <TableRow key={row.name}>
                        <TableCell className="font-medium">{row.name}</TableCell>
                        <TableCell className="text-right">{row.simulations.toLocaleString("fr-FR")}</TableCell>
                        <TableCell className="text-right">{row.cotations.toLocaleString("fr-FR")}</TableCell>
                        <TableCell className="text-right">{row.proposals.toLocaleString("fr-FR")}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Pie charts */}
        <div className="grid gap-6 lg:grid-cols-3">
          <PieChartCard
            title="Répartition des simulations par type"
            data={simulationsPieData}
            colorMap={colorMap}
            isLoading={isLoadingStats}
            delay="0.25s"
          />
          <PieChartCard
            title="Répartition des cotations par typologie"
            data={cotationsPieData}
            colorMap={colorMap}
            isLoading={isLoadingStats}
            delay="0.3s"
          />
          <PieChartCard
            title="Répartition des propositions par typologie"
            data={proposalsPieData}
            colorMap={colorMap}
            isLoading={isLoadingStats}
            delay="0.35s"
          />
        </div>

        {/* Top users + Recent activity */}
        <div className={`grid gap-6 ${isBE ? "" : "lg:grid-cols-2"}`}>
          {/* Top users — masqué pour les BE (suivi utilisateurs fermé) */}
          {!isBE && (
          <div style={{ animation: "fadeInUp 0.5s ease-out 0.4s both" }}>
            <Card className="h-full">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Users className="h-5 w-5" />
                  Top utilisateurs
                </CardTitle>
                <CardDescription>Classement par nombre de cotations sur la période</CardDescription>
              </CardHeader>
              <CardContent>
                {isLoadingTopUsers ? (
                  <div className="space-y-3">
                    {[...Array(5)].map((_, i) => (
                      <Skeleton key={i} className="h-10 w-full" />
                    ))}
                  </div>
                ) : !topUsers?.length ? (
                  <p className="text-center py-6 text-muted-foreground">Aucune donnée disponible</p>
                ) : (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Utilisateur</TableHead>
                        <TableHead className="text-right">Cotations</TableHead>
                        <TableHead className="text-right">Propales</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {topUsers.map((u, i) => (
                        <TableRow key={u.user_id}>
                          <TableCell>
                            <div className="flex items-center gap-2">
                              <span className="flex items-center justify-center h-6 w-6 rounded-full bg-primary/10 text-primary text-xs font-bold">
                                {i + 1}
                              </span>
                              <span className="font-medium truncate max-w-[160px]" title={u.email}>
                                {u.full_name}
                              </span>
                            </div>
                          </TableCell>
                          <TableCell className="text-right">{u.cotations}</TableCell>
                          <TableCell className="text-right">{u.proposals}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
              </CardContent>
            </Card>
          </div>
          )}

          {/* Recent activity feed */}
          <div style={{ animation: "fadeInUp 0.5s ease-out 0.45s both" }}>
            <Card className="h-full">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Clock className="h-5 w-5" />
                  Activité récente
                </CardTitle>
                <CardDescription>Dernières actions dans l'outil</CardDescription>
              </CardHeader>
              <CardContent>
                {isLoadingActivity ? (
                  <div className="space-y-3">
                    {[...Array(5)].map((_, i) => (
                      <Skeleton key={i} className="h-12 w-full" />
                    ))}
                  </div>
                ) : !recentActivity?.length ? (
                  <p className="text-center py-6 text-muted-foreground">Aucune activité récente</p>
                ) : (
                  <div className="space-y-3 max-h-[400px] overflow-auto pr-1">
                    {recentActivity.map((a) => (
                      <div key={a.id} className="flex items-start gap-3 text-sm">
                        <div className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-muted">
                          <ActivityIcon action={a.action} />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-foreground">
                            <span className="font-medium">{a.action_label}</span>{" "}
                            <span className="text-muted-foreground">{a.entity_type_label}</span>{" "}
                            <span className="font-medium truncate">{a.entity_name}</span>
                          </p>
                          <p className="text-xs text-muted-foreground">
                            {a.user?.full_name || a.user?.email || "Système"} · {formatRelativeTime(a.created_at)}
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </div>

        {/* Agency stats */}
        <div style={{ animation: "fadeInUp 0.5s ease-out 0.5s both" }}>
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Building2 className="h-5 w-5" />
                Statistiques par agence
              </CardTitle>
              <CardDescription>Cotations et propositions par agence / région</CardDescription>
            </CardHeader>
            <CardContent>
              {isLoadingAgency ? (
                <div className="space-y-3">
                  {[...Array(3)].map((_, i) => (
                    <Skeleton key={i} className="h-10 w-full" />
                  ))}
                </div>
              ) : !agencyStats?.length ? (
                <p className="text-center py-6 text-muted-foreground">Aucune donnée disponible</p>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Agence</TableHead>
                      <TableHead>Région</TableHead>
                      <TableHead className="text-right">Cotations</TableHead>
                      <TableHead className="text-right">Propositions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {agencyStats.map((a) => (
                      <TableRow key={a.agency_id}>
                        <TableCell className="font-medium">{a.agency_name}</TableCell>
                        <TableCell className="text-muted-foreground">{a.group_name || "—"}</TableCell>
                        <TableCell className="text-right">{a.cotations}</TableCell>
                        <TableCell className="text-right">{a.proposals}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Offer × region stats */}
        <div style={{ animation: "fadeInUp 0.5s ease-out 0.55s both" }}>
          <OfferRegionStatsCard filterParams={filterParams} />
        </div>
      </div>
    </div>
    </ViewerGuard>
  );
}


function PieChartCard({
  title,
  data,
  colorMap,
  isLoading,
  delay,
}: {
  title: string;
  data: { name: string; value: number }[];
  colorMap: Record<string, string>;
  isLoading: boolean;
  delay: string;
}) {
  return (
    <div style={{ animation: `fadeInUp 0.5s ease-out ${delay} both` }}>
      <Card>
        <CardHeader>
          <CardTitle className="text-base">{title}</CardTitle>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <Skeleton className="h-[300px] w-full" />
          ) : data.length === 0 ? (
            <p className="text-center py-12 text-muted-foreground">Aucune donnée disponible</p>
          ) : (
            <ResponsiveContainer width="100%" height={300}>
              <PieChart>
                <Pie
                  data={data}
                  cx="50%"
                  cy="50%"
                  innerRadius={60}
                  outerRadius={100}
                  paddingAngle={2}
                  dataKey="value"
                >
                  {data.map((entry) => (
                    <Cell key={entry.name} fill={colorMap[entry.name]} />
                  ))}
                </Pie>
                <Tooltip />
                <Legend />
              </PieChart>
            </ResponsiveContainer>
          )}
        </CardContent>
      </Card>
    </div>
  );
}


function ActivityIcon({ action }: { action: string }) {
  switch (action) {
    case "create":
      return <TrendingUp className="h-4 w-4 text-success" />;
    case "update":
      return <Activity className="h-4 w-4 text-info" />;
    case "delete":
      return <AlertTriangle className="h-4 w-4 text-destructive" />;
    case "publish":
      return <FileText className="h-4 w-4 text-primary" />;
    default:
      return <Activity className="h-4 w-4 text-muted-foreground" />;
  }
}


function formatRelativeTime(isoDate: string): string {
  const diff = Date.now() - new Date(isoDate).getTime();
  const minutes = Math.floor(diff / 60000);
  if (minutes < 1) return "à l'instant";
  if (minutes < 60) return `il y a ${minutes} min`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `il y a ${hours}h`;
  const days = Math.floor(hours / 24);
  if (days < 7) return `il y a ${days}j`;
  return new Date(isoDate).toLocaleDateString("fr-FR");
}
